import './spotlightSection.scss';
import React, {useCallback, useEffect, useState} from 'react';
import {
    useSelectedDocContext,
    useStateDocumentSelectedTags,
    useStateExpandedSpotlightGroups,
    useStateIsDocumentLoading,
} from '../../../search-hooks';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {
    resetTransformAction,
    setCurrentPageNumberAction,
    setExpandedSpotlightGroups,
    setHighlightIdAction,
    showAnnotations,
} from '../../../duck/search-actions';
import classNames from 'clsx';
import {Button, OverlayTrigger, Popover} from 'react-bootstrap';
import {SvgIcon} from '../../../../icons/svgIcon';
import {Icons} from '../../../../icons/icons';
import {useTranslation} from 'react-i18next';
import {PdfHighlight} from '../../../../common/pdf-viewer/PdfViewer.types';
import {SpotlightTagType, TagKey} from '../../../../config/config-types';
import {FilterWidget} from '../../../../common/filterWidget/filterWidget';
import {useLocalizationUtil} from '../../../../localizationUtil';
import {Spinner} from '../../../../common/spinner/spinner';
import {HibuButton} from '../../../../common/hibuButton/hibuButton';

export const SpotlightSection = React.memo(() => {
    const annotations = useStateDocumentSelectedTags();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const isDocumentLoading = useStateIsDocumentLoading();
    const selectedDocument = useSelectedDocContext();

    const {spotlightTagTypes} = useSelector((state: DefaultRootState) => ({spotlightTagTypes: state.config.uiConfig.search.spotlightTagTypes}));

    useEffect(() => {
        if (selectedDocument) {
            dispatch(showAnnotations(true));
        }
        return () => {
            dispatch(showAnnotations(false));
        };
    }, [dispatch, selectedDocument]);

    const getTagTypeGroups = useCallback(() => {
        const tagTypeGroups = new Map<SpotlightTagType, PdfHighlight[]>();
        spotlightTagTypes.forEach((htt) => {
            const tagTypeGroup: PdfHighlight[] = [];
            annotations.forEach((annotation) => {
                if (annotation.contentsObj.str.endsWith(htt.name)) {
                    tagTypeGroup.push(annotation);
                }
            });
            if (tagTypeGroup.length > 0) tagTypeGroups.set(htt, tagTypeGroup);
        });
        return tagTypeGroups;
    }, [annotations, spotlightTagTypes]);

    const tagTypeGroups = getTagTypeGroups();

    const renderedAnnotations = useCallback(() => {
        const rendered: React.ReactNode[] = [];

        tagTypeGroups.forEach((annotations, spotlightTagType) => {
            const tagKeys = spotlightTagTypes.find((tk) => tk === spotlightTagType)?.tagKeys;

            rendered.push(
                <SpotlightSectionElement
                    spotlightTagType={spotlightTagType}
                    annotations={annotations}
                    tagKeys={tagKeys}
                />,
            );
            return rendered;
        });
        return rendered;
    }, [tagTypeGroups, spotlightTagTypes]);

    if (isDocumentLoading || !annotations) {
        return <Spinner/>; // if document is loading
    } else if (tagTypeGroups.size === 0) {
        return <div>{t('search.details.noAnnotations')}</div>; // after document loaded
    } else {
        return <div id={'hibu-details-selected-tags'}>{renderedAnnotations()}</div>;
    }
});

type SpotlightSectionElementProps = {
    spotlightTagType: SpotlightTagType;
    annotations: PdfHighlight[];
    tagKeys: undefined | TagKey[];
};
const SpotlightSectionElement = React.memo(({spotlightTagType, annotations, tagKeys}: SpotlightSectionElementProps) => {
    const dispatch = useDispatch();
    const [filter, setFilter] = useState('');
    const expandedSpotlightGroups: Record<string, boolean> = useStateExpandedSpotlightGroups();
    const isExpanded = expandedSpotlightGroups[spotlightTagType.name] ?? false;
    const [grouped, setGrouped] = React.useState(false);
    const {t} = useTranslation();
    const contentId = `spotlight-tag-type-${spotlightTagType.name}`;

    const onGroup = () => {
        setGrouped(!grouped);
    };

    const ungroupedContent = useCallback(() => {
        return <AnnotationGroup annotations={annotations} filter={filter} tagKeys={tagKeys}
                                grouped={false}></AnnotationGroup>;
    }, [annotations, filter, tagKeys]);

    const groupedContent = useCallback(() => {
        const groupedHighlights: Record<string, PdfHighlight[]> = annotations.reduce<Record<string, PdfHighlight[]>>(
            (groups, highlight) => {
                const groupId = highlight.id.replace(/#\d+$/, ''); // Remove the trailing #number
                if (!groups[groupId]) {
                    groups[groupId] = [];
                }
                groups[groupId].push(highlight);
                return groups;
            },
            {}
        );
        return (
            <div className={'grouped-content'}>
                {Object.entries(groupedHighlights)
                    .filter(([, groupedAnnotations]: [string, PdfHighlight[]]) => !filter || groupedAnnotations[0].titleObj.str.toLowerCase().includes(filter.toLowerCase()))
                    .map(([groupId, groupedAnnotations]: [string, PdfHighlight[]]) => (
                        <GroupedContent key={groupId} groupId={groupId} groupedAnnotations={groupedAnnotations}
                                        filter={filter} tagKeys={tagKeys}></GroupedContent>
                    ))}
            </div>
        );
    }, [annotations, filter, tagKeys]);

    return (
        <div key={'id_' + spotlightTagType.name}>
            <div className={'selected-tags-header'}>
                <Button
                    type={'button'}
                    className={classNames('toggle-header')}
                    aria-expanded={isExpanded}
                    aria-controls={contentId}
                    onClick={() => dispatch(setExpandedSpotlightGroups(spotlightTagType.name, !isExpanded))}
                >
                    <SvgIcon icon={Icons.CHEVRON_DOWN} className={isExpanded ? 'expanded' : ''}/>
                    <span className={'toggle-label'}>{t(`fieldTitle.tagsPlural.${spotlightTagType.name}.label`)}</span>
                </Button>
                {isExpanded && (
                    <div className={'selected-tags-actions'}>
                        <HibuButton visible={true} onClick={onGroup} icon={Icons.GROUPING}
                                    tooltipKey={grouped ? 'search.details.ungroupAnnotations' : 'search.details.groupAnnotations'}
                                    className={grouped ? 'grouping-enabled' : 'grouping-disabled'}/>
                        <FilterWidget
                            filter={filter}
                            setFilter={setFilter}
                            title={t(`fieldTitle.tagsPlural.${spotlightTagType.name}.label`)}
                        />
                    </div>
                )}
            </div>
            {isExpanded && <div id={contentId}>{grouped ? groupedContent() : ungroupedContent()}</div>}
        </div>
    );
});

type GroupedContentProps = {
    groupId: string;
    groupedAnnotations: PdfHighlight[];
    filter: string;
    tagKeys: undefined | TagKey[];
};

const GroupedContent = ({groupId, groupedAnnotations, filter, tagKeys}: GroupedContentProps) => {
    const [expanded, setExpanded] = useState(false);
    const contentId = React.useId();

    return (
        <div key={groupId}>
            <Button
                type={'button'}
                className={classNames('toggle-header')}
                aria-expanded={expanded}
                aria-controls={contentId}
                onClick={() => setExpanded((isExpanded) => !isExpanded)}
            >
                <SvgIcon icon={Icons.CHEVRON_DOWN} className={expanded ? 'expanded' : ''}/>
                <span className={'toggle-label'}>{groupedAnnotations[0].titleObj.str}</span>
            </Button>
            {expanded && (
                <div id={contentId}>
                    <AnnotationGroup key={groupId} annotations={groupedAnnotations} filter={filter}
                                     tagKeys={tagKeys} grouped={true}></AnnotationGroup>
                </div>
            )}
        </div>
    );
};

type AnnotationGroupProps = {
    annotations: PdfHighlight[];
    filter: string;
    tagKeys: undefined | TagKey[];
    grouped: boolean;
};

const AnnotationGroup = ({annotations, filter, tagKeys, grouped}: AnnotationGroupProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {formatNumber} = useLocalizationUtil();

    const onClickAnnotation = useCallback(
        (pageNumber: number, highlightId: string) => {
            dispatch(resetTransformAction(true));
            dispatch(setCurrentPageNumberAction(pageNumber));
            dispatch(setHighlightIdAction(highlightId, true));
        },
        [dispatch],
    );

    const popoverContent = useCallback(
        (annotation: PdfHighlight) => {
            if (!tagKeys) {
                return <div></div>;
            }
            const tagKey = tagKeys.find((tk) => tk.name === annotation.titleObj.str);

            if (!tagKey) {
                return <div></div>;
            }

            const name = tagKey.name;
            const popover = (
                <Popover key={'key_' + name} id={'popover-' + name}>
                    <Popover.Header as="h4">{name}</Popover.Header>
                    <Popover.Body>{tagKey?.description}</Popover.Body>
                </Popover>
            );

            return (
                <OverlayTrigger trigger="click" placement="left" overlay={popover} rootClose>
                    <Button className={classNames('tagKey-info-button', 'toggle-header')}>
                        <SvgIcon icon={Icons.QUESTION} />
                    </Button>
                </OverlayTrigger>
            );
        },
        [tagKeys],
    );

    return (
        <ul className={classNames('selected-tags-list')}>{annotations
            .filter((annotation) => !filter || annotation.titleObj.str.toLowerCase().includes(filter.toLowerCase()))
            .map((annotation) => (
                <li key={'id_' + annotation.id} className={classNames('selected-tags-element')}>
                    <Button className={'selected-tags-container'}
                            onClick={() => onClickAnnotation(annotation.page, annotation.id)}>
                        <span
                            className={'selected-tags-title'}>{grouped ? t('search.details.pageNumber') : annotation.titleObj.str}</span>
                        <span className={'selected-tags-page'}>{formatNumber(annotation.page)}</span>
                    </Button>
                    {popoverContent(annotation)}
                </li>
            ))}
        </ul>
    );
};
